import * as React from "react";
import { Button, Card, CardContent, Skeleton } from "@ibada/ui";

import { NotificationEmptyState } from "@/features/notifications/components/notification-empty-state";
import { NotificationItem } from "@/features/notifications/components/notification-item";
import { useNotificationWorkspaceLabelMap } from "@/features/notifications/hooks/use-notification-workspace-label-map";
import {
  groupNotificationsByTime,
  type NotificationTimeBucket,
} from "@/features/notifications/lib/group-notifications-by-time";
import { type NotificationDto } from "@/features/notifications/model/notifications.types";

type NotificationListProps = {
  items?: NotificationDto[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onItemSelect?: (notification: NotificationDto) => void | Promise<void>;
  onArchive?: (notification: NotificationDto) => void | Promise<void>;
  onMarkRead?: (notification: NotificationDto) => void | Promise<void>;
  archivingNotificationId?: string | null;
  emptyTitle: string;
  emptyDescription: string;
  errorTitle: string;
  errorDescription: string;
  retryLabel: string;
  compact?: boolean;
  showArchiveAction?: boolean;
  itemSurface?: "card" | "plain";
  showWorkspaceContext?: boolean;
  className?: string;
  /** When set, items are split into Today / Yesterday / This week / Earlier sections with sticky headers. */
  groupByTime?: boolean;
  /** Localized labels for each recency bucket — required when `groupByTime` is on. */
  sectionLabels?: Record<NotificationTimeBucket, string>;
};

function NotificationListSkeleton({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <div className="space-y-2 py-2">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="grid grid-cols-[0.5rem_minmax(0,1fr)] gap-3 px-4 py-2.5">
            <Skeleton className="mt-1.5 size-2 rounded-full" />
            <div className="min-w-0 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-3 w-14" />
              </div>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {Array.from({ length: 6 }).map((_, index) => (
        <Card key={index} size="sm" className="border-border/50 ring-0">
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-4 w-2/5" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-4/5" />
            <Skeleton className="h-5 w-24 rounded-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function NotificationList({
  items,
  isLoading = false,
  isError = false,
  onRetry,
  onItemSelect,
  onArchive,
  onMarkRead,
  archivingNotificationId,
  emptyTitle,
  emptyDescription,
  errorTitle,
  errorDescription,
  retryLabel,
  compact = false,
  showArchiveAction = false,
  itemSurface = "card",
  showWorkspaceContext = true,
  className,
  groupByTime = false,
  sectionLabels,
}: NotificationListProps) {
  const workspaceLabelMap = useNotificationWorkspaceLabelMap();

  const renderItem = React.useCallback(
    (notification: NotificationDto) => (
      <NotificationItem
        key={notification.id}
        notification={notification}
        workspaceLabel={workspaceLabelMap.get(notification.workspaceId) ?? null}
        onSelect={onItemSelect}
        onArchive={onArchive}
        onMarkRead={onMarkRead}
        isArchiving={archivingNotificationId === notification.id}
        compact={compact}
        showArchiveAction={showArchiveAction}
        surface={itemSurface}
        showWorkspaceContext={showWorkspaceContext}
      />
    ),
    [archivingNotificationId, compact, itemSurface, onArchive, onItemSelect, onMarkRead, showArchiveAction, showWorkspaceContext, workspaceLabelMap],
  );

  const itemsContainerClass = itemSurface === "plain" ? "divide-y divide-border/50" : "space-y-2.5";
  // Compact rows carry their own horizontal padding (full-bleed hover), so the standalone states need an inset.
  const stateClassName = [compact ? "mx-3" : null, className].filter(Boolean).join(" ") || undefined;

  if (isLoading) {
    return <NotificationListSkeleton compact={compact} />;
  }

  if (isError) {
    return (
      <NotificationEmptyState
        className={stateClassName}
        compact={compact}
        tone="danger"
        title={errorTitle}
        description={errorDescription}
        action={onRetry ? <Button variant="outline" onClick={onRetry}>{retryLabel}</Button> : undefined}
      />
    );
  }

  if (!items?.length) {
    return (
      <NotificationEmptyState
        className={stateClassName}
        compact={compact}
        title={emptyTitle}
        description={emptyDescription}
      />
    );
  }

  if (groupByTime && sectionLabels) {
    const groups = groupNotificationsByTime(items);
    return (
      <div className={className}>
        {groups.map((group) => (
          <section key={group.bucket} className={compact ? "pb-1" : undefined}>
            <h3 className={compact
              ? "text-muted-foreground/75 px-4 pb-1.5 pt-3 text-[0.68rem] font-bold uppercase tracking-[0.14em]"
              : "bg-muted/30 text-muted-foreground sticky top-0 z-10 px-2 py-1.5 text-xs font-semibold uppercase tracking-wide backdrop-blur supports-[backdrop-filter]:bg-muted/20"}
            >
              {sectionLabels[group.bucket]}
            </h3>
            <div className={itemsContainerClass}>{group.items.map(renderItem)}</div>
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className={className}>
      <div className={itemsContainerClass}>{items.map(renderItem)}</div>
    </div>
  );
}
